import { css } from '@emotion/css';
import { AutocompleteClasses } from '@mui/material/Autocomplete';
import c from 'classnames';
import mergeWith from 'lodash/mergeWith';

import { SelectSize } from '../types';

const autocompletePopperStyles = css`
  z-index: 1300;
`;

const selectOverrideStyles = css`
  .MuiAutocomplete-endAdornment {
    top: unset;
    right: 1.2rem;
    [dir='rtl'] & {
      right: unset;
      left: 1.2rem;
    }

    .MuiButton-root {
      padding: 0;
    }
  }

  &.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root,
  &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root {
    padding-inline-end: 3.2rem;
  }

  &.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root {
    padding-inline-end: 6.4rem;
  }

  .MuiOutlinedInput-root.Mui-focused {
    .MuiAutocomplete-clearIndicator {
      visibility: visible;
    }
  }

  @media (pointer: fine) {
    &:hover .MuiAutocomplete-clearIndicator {
      visibility: visible;
    }
  }

  .MuiOutlinedInput-input {
    width: 0;
    min-width: 3rem;
  }

  .MuiOutlinedInput-adornedStart {
    padding-inline-start: 1.2rem;
  }
`;

export const optionOverrideStyles = css`
  &.MuiAutocomplete-option[data-focus="true"],
  &.Mui-focused {
    background-color: var(--bg-tertiary);
  }

  &.MuiAutocomplete-option[aria-disabled="true"] {
    pointer-events: none;
  },
`;

const listBoxStyles = css`
  max-height: 40vh;
`;

const popperStyles = css`
  .MuiPaper-root:empty {
    opacity: 0;
  }
`;

const AUTOCOMPLETE_STATIC_CLASSES: Partial<AutocompleteClasses> = {
  clearIndicator: 'MuiAutocomplete-clearIndicator',
  endAdornment: 'MuiAutocomplete-endAdornment',
  groupLabel: 'MuiAutocomplete-groupLabel',
  groupUl: 'MuiAutocomplete-groupUl',
  hasClearIcon: 'MuiAutocomplete-hasClearIcon',
  hasPopupIcon: 'MuiAutocomplete-hasPopupIcon',
  input: 'MuiAutocomplete-input',
  inputRoot: 'MuiAutocomplete-inputRoot',
  listbox: c('MuiAutocomplete-listbox list-none overflow-auto m-0', listBoxStyles),
  loading: 'MuiAutocomplete-loading',
  noOptions: 'MuiAutocomplete-noOptions flex items-center',
  option: 'MuiAutocomplete-option flex items-center cursor-pointer p-0',
  paper: 'MuiAutocomplete-paper',
  popper: c('MuiAutocomplete-popper', autocompletePopperStyles),
  popupIndicator: 'MuiAutocomplete-popupIndicator',
  popupIndicatorOpen: 'MuiAutocomplete-popupIndicatorOpen',
  root: 'MuiAutocomplete-root',
  tag: 'MuiAutocomplete-tag',
};

const iconSizeStyles = (size: number) => css`
  svg {
    width: ${size}rem;
    height: ${size}rem;
  }
`;

const ICON_CLASSES_CONFIG = {
  large: iconSizeStyles(2.4),
  medium: iconSizeStyles(2),
};

const getDefaultClasses = (size: SelectSize): Partial<AutocompleteClasses> => ({
  clearIndicator: c('invisible', ICON_CLASSES_CONFIG[size]),
  endAdornment: 'absolute',
  input: 'py-4 flex-1',
  inputRoot: 'w-full flex-wrap py-4',
  listbox: 'p-8',
  loading: 'py-14 px-16 body-short-02 text-text-secondary',
  noOptions: 'py-14 px-16 body-short-02 text-text-secondary',
  option: c('bg-bg-primary rounded-8 hover:bg-bg-tertiary', optionOverrideStyles),
  paper: 'rounded-8 shadow-01 border border-divider my-8',
  popper: popperStyles,
  popupIndicator: ICON_CLASSES_CONFIG[size],
  popupIndicatorOpen: 'transform rotate-180',
  root: selectOverrideStyles,
  tag: 'm-2 bg-bg-tertiary',
});

export const getAutocompleteClasses = (
  size: SelectSize,
  classes?: Partial<AutocompleteClasses>,
) => {
  const mergedClasses = { ...getDefaultClasses(size), ...classes };

  return mergeWith({}, AUTOCOMPLETE_STATIC_CLASSES, mergedClasses, (c1, c2) => c(c1, c2));
};
